import {
  ArrowLeft,
  BarChart3,
  Keyboard,
  MessageSquare,
  Plus,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import CompareLapMenu from "@/components/analytics/CompareLapMenu";
import type { TelemetryLapSummary } from "@/types";

interface AnalyticsHeaderProps {
  sessionId: string;
  sessionTitle: string;
  isMobileLayout: boolean;
  hasTelemetryMetadata: boolean;
  totalDataPoints: number;
  selectedChannelCount: number;
  laps: TelemetryLapSummary[];
  currentTime: number;
  showNotesControls: boolean;
  noteCount: number;
  showNotesPanel: boolean;
  onToggleNotesPanel: () => void;
  onAddNoteAtCurrentTime: () => void;
  hasUsableTimeline: boolean;
  onBack: () => void;
}

export default function AnalyticsHeader({
  sessionId,
  sessionTitle,
  isMobileLayout,
  hasTelemetryMetadata,
  totalDataPoints,
  selectedChannelCount,
  laps,
  currentTime,
  showNotesControls,
  noteCount,
  showNotesPanel,
  onToggleNotesPanel,
  onAddNoteAtCurrentTime,
  hasUsableTimeline,
  onBack,
}: AnalyticsHeaderProps) {
  const { t } = useTranslation(["pages", "common"]);

  return (
 <div className="flex-shrink-0 bg-[#0a0a0f] border-b border-[#1a1a2e] px-5 py-3">
 <div className="flex items-center justify-between">
 <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              aria-label={t("common:back")}
 className="p-2 text-[#4b5563] hover:text-[#94a3b8] hover:bg-[#111827] rounded-lg transition-colors"
            >
 <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-base font-semibold text-slate-200 truncate">
                {sessionTitle}
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {t("pages:sessions.analytics.subtitle")}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {!isMobileLayout && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#0f172a] text-[#4b6bfb] border border-[#1e3a8a]">
                <BarChart3 className="h-3 w-3" />
                {t("pages:sessions.analytics.title")}
              </span>
            )}
            {!isMobileLayout && hasTelemetryMetadata && (
              <div className="flex items-center gap-1.5 ml-2">
                <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md bg-[#111827] border border-[#1f2937] text-[#6b7280]">
                  {selectedChannelCount}{t("pages:sessions.analytics.channelsSuffix", { count: selectedChannelCount })}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md bg-[#111827] border border-[#1f2937] text-[#6b7280]">
                  {totalDataPoints > 999
                    ? `${(totalDataPoints / 1000).toFixed(0)}${t("pages:sessions.analytics.pointsThousandSuffix")}`
                    : `${totalDataPoints}${t("pages:sessions.analytics.pointsSuffix")}`}
                </span>
              </div>
            )}

            <CompareLapMenu
              sessionId={sessionId}
              laps={laps}
              currentTime={currentTime}
              compact={isMobileLayout}
            />

            {/* Telemetry Notes Controls */}
            {showNotesControls && (
              <div className="flex items-center space-x-2 border-l border-[#1a1a2e] pl-4">
                <button
                  onClick={onToggleNotesPanel}
                  className={`relative p-2 rounded-md transition-colors ${
                    showNotesPanel
                      ? "bg-[#1e0a3c] text-purple-400 border border-[#3b1d5e]"
                      : "text-[#4b5563] hover:text-purple-400 hover:bg-[#1e0a3c]"
                  }`}
                  title={t("pages:sessions.analytics.showNotesPanel")}
                >
                  <MessageSquare className="h-4 w-4" />
                  {noteCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#7c3aed] text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                      {noteCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={onAddNoteAtCurrentTime}
                  disabled={!hasUsableTimeline}
                  className={`p-2 rounded-md transition-colors ${
                    hasUsableTimeline
                      ? "text-[#4b5563] hover:text-[#4ade80] hover:bg-[#052e16]"
                      : "text-[#374151] cursor-not-allowed"
                  }`}
                  title={
                    hasUsableTimeline
                      ? t("pages:sessions.analytics.addNoteAtCurrentTime")
                      : t("pages:sessions.analytics.addNoteWaitingForTimeline")
                  }
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            )}

            {!isMobileLayout && (
            <div className="relative group">
            <Keyboard className="h-4 w-4 text-[#4b5563] hover:text-[#6b7280] cursor-help" />
            <div className="absolute right-0 top-6 w-80 bg-[#0d0d14] border border-[#1a1a2e] text-white text-xs rounded-lg shadow-lg p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
            <div className="font-semibold mb-2">{t("pages:sessions.analytics.keyboardShortcuts")}</div>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span>←/→</span>
                <span>{t("pages:sessions.analytics.shortcutVideoSeek1s")}</span>
              </div>
              <div className="flex justify-between">
                <span>Shift + ←/→</span>
                <span>{t("pages:sessions.analytics.shortcutVideoSeek10s")}</span>
              </div>
              <div className="flex justify-between">
                <span>Spacebar</span>
                <span>{t("pages:sessions.analytics.shortcutPlayPause")}</span>
              </div>
              <div className="flex justify-between">
                <span>J/K</span>
                <span>{t("pages:sessions.analytics.shortcutScrollChart")}</span>
              </div>
              <div className="flex justify-between">
                <span>+/-</span>
                <span>{t("pages:sessions.analytics.shortcutZoomChart")}</span>
              </div>
              <div className="flex justify-between">
                <span>I/O</span>
                <span>{t("pages:sessions.analytics.shortcutChartHeight")}</span>
              </div>
            </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
  );
}
